import { useAntTheme } from "hooks/useAntTheme/";
import { BrandKit } from "../types";
import { Flex, Typography } from "antd";
import { ImageVideoPreview } from "./storyboardEditor/components/common/ImageVideoPreview";
import { ITEM_TYPES } from "./storyboardEditor/types";
import { ColorPreviewBlock } from "../brandkit/components/preview/ColorPreviewBlock";
import { FontPreviewText } from "../brandkit/components/FontPreview";

const getBrandKitLogo = (brandKit: BrandKit) =>
  brandKit.assets.find(
    (asset) => asset.type === "Picture" && asset.purpose === "Logo"
  );

const getBrandKitColors = (brandKit: BrandKit) =>
  [
    brandKit.palette.primary_color,
    brandKit.palette.secondary_color,
    brandKit.palette.title_color,
  ].filter((c): c is string => !!c);

const getBrandKitFonts = (brandKit: BrandKit) =>
  brandKit.assets.filter((asset) => asset.type === "Font");

export const SmallBrandKitPreview = ({
  brandKit,
  compact = false,
  showName = true,
}: {
  brandKit: BrandKit;
  compact?: boolean;
  showName?: boolean;
}) => {
  const { theme } = useAntTheme();
  const logo = getBrandKitLogo(brandKit);
  const colors = getBrandKitColors(brandKit);
  const fonts = getBrandKitFonts(brandKit);

  const sizes = compact
    ? { color: 16, logo: 32, fontSizeSmall: 12, fontSizeLarge: 13, gap: 6 }
    : { color: 24, logo: 48, fontSizeSmall: 13, fontSizeLarge: 14, gap: 10 };

  return (
    <Flex
      vertical
      gap={sizes.gap}
      style={{ width: "100%", padding: compact ? "4px 0" : 0 }}
    >
      <Flex align="center" gap={10}>
        {logo && (
          <div
            style={{
              width: sizes.logo,
              height: sizes.logo,
              borderRadius: 6,
              overflow: "hidden",
              flexShrink: 0,
              border: `1px solid ${theme.colorBorder}`,
            }}
          >
            <ImageVideoPreview
              type={ITEM_TYPES.Image}
              path={logo.path}
              assetId={logo.asset_id}
              style={{
                objectFit: "contain",
                width: sizes.logo,
                height: sizes.logo,
              }}
            />
          </div>
        )}
        {showName && (
          <Typography.Text
            strong={!compact}
            style={{
              flex: 1,
              fontSize: sizes.fontSizeLarge,
              wordBreak: "break-word",
            }}
          >
            {brandKit.name}
          </Typography.Text>
        )}
      </Flex>
      {colors.length > 0 && (
        <Flex gap={6} align="center">
          {colors.map((color, i) => {
            const occurrence = colors
              .slice(0, i)
              .filter((c) => c === color).length;
            return (
              <ColorPreviewBlock
                key={`${color}-${occurrence}`}
                color={color}
                size={sizes.color}
              />
            );
          })}
        </Flex>
      )}
      {fonts.length > 0 && (
        <Flex vertical gap={2}>
          {fonts.map((font) => (
            <FontPreviewText
              key={`${font.asset_id}-${font.purpose}`}
              font={font.path}
              text={font.purpose}
              style={{
                fontSize: sizes.fontSizeSmall,
                textAlign: "left",
                padding: 0,
              }}
            />
          ))}
        </Flex>
      )}
    </Flex>
  );
};
